<script setup lang="ts">
import { ref } from 'vue'
import { mockResources, mockIdols, mockGroups, mockAgency } from '../mock/gameState'
import EventScreen from './EventScreen.vue'
import RecruitPanel from './RecruitPanel.vue'
import ResultScreen from './ResultScreen.vue'

const showEvent = ref(false)
const showRecruit = ref(false)
const showResult = ref(false)
const selectedIdolId = ref<string | null>(null)

const actions = [
  { name: '训练', cost: 1, icon: '🎤', desc: '提升偶像属性', color: 'text-accent-cyan', action: () => {} },
  { name: '制作单曲', cost: 2, icon: '💿', desc: '增加名气和粉丝', color: 'text-accent-purple', action: () => {} },
  { name: '举办演出', cost: 2, icon: '🎪', desc: '获得收入和人气', color: 'text-primary', action: () => {} },
  { name: '商业合作', cost: 1, icon: '💼', desc: '获取资金', color: 'text-accent-gold', action: () => {} },
  { name: '招募试镜', cost: 1, icon: '🔍', desc: '招募新偶像', color: 'text-info', action: () => showRecruit.value = true },
  { name: '事务所升级', cost: 2, icon: '🏢', desc: '提升事务所等级', color: 'text-accent-coral', action: () => {} },
]

const statusConfig: Record<string, { icon: string; label: string; class: string }> = {
  active: { icon: '✨', label: '状态良好', class: 'text-accent-cyan' },
  tired: { icon: '😴', label: '疲惴', class: 'text-accent-coral' },
  injured: { icon: '🤕', label: '受伤', class: 'text-danger' },
  sad: { icon: '💔', label: '低落', class: 'text-accent-purple' },
  breakthrough: { icon: '🌟', label: '突破中', class: 'text-accent-gold' },
}

const rarityColors: Record<string, string> = {
  N: 'border-rarity-n text-rarity-n',
  R: 'border-rarity-r text-rarity-r',
  SR: 'border-rarity-sr text-rarity-sr',
  SSR: 'border-accent-gold text-accent-gold',
}

const rarityBg: Record<string, string> = {
  N: 'from-[#3a3a4a] to-[#2a2a3a]',
  R: 'from-[#1e3a6e] to-[#162d54]',
  SR: 'from-[#4a3a00] to-[#2d2200]',
  SSR: 'from-[#4a1a2e] via-[#2a1a4a] to-[#3a2800]',
}

function formatNumber(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
  return n.toLocaleString()
}

function staminaColor(val: number): string {
  if (val <= 20) return '#FF4560'
  if (val <= 50) return '#FF7F5C'
  return '#06D6A0'
}

function getIdolGroup(groupId: string | null): string | null {
  if (!groupId) return null
  return mockGroups.find(g => g.id === groupId)?.name ?? null
}
</script>

<template>
  <div class="flex h-full min-h-0">
    <!-- 左栏：偶像区 -->
    <div class="w-64 xl:w-72 flex-shrink-0 border-r border-accent-purple border-opacity-10 flex flex-col bg-bg-base bg-opacity-50">
      <div class="px-4 py-3 border-b border-accent-purple border-opacity-10 flex items-center justify-between">
        <h3 class="text-text-primary text-sm font-semibold">我的偶像</h3>
        <span class="text-xs text-text-muted bg-bg-surface px-2 py-0.5 rounded-full">{{ mockIdols.length }} 人</span>
      </div>

      <div class="flex-1 overflow-y-auto p-3 space-y-2">
        <!-- 偶像卡片 -->
        <button
          v-for="idol in mockIdols"
          :key="idol.id"
          @click="selectedIdolId = selectedIdolId === idol.id ? null : idol.id"
          class="idol-card w-full"
          :class="selectedIdolId === idol.id ? 'idol-card--selected' : ''"
        >
          <div class="flex items-start gap-3">
            <!-- 立绘占位 -->
            <div class="relative flex-shrink-0">
              <div
                class="w-14 rounded-lg bg-gradient-to-b overflow-hidden border flex items-end justify-center"
                :class="[rarityBg[idol.rarity], idol.rarity === 'SSR' ? 'border-accent-gold border-opacity-50' : 'border-bg-elevated']"
                style="height: 72px"
              >
                <div class="w-10 h-14 idol-silhouette opacity-30" />
              </div>
              <span
                class="absolute -top-1 -left-1 text-[10px] font-bold px-1 py-0.5 rounded border leading-none"
                :class="rarityColors[idol.rarity]"
                style="background: rgba(13,10,26,0.9)"
              >{{ idol.rarity }}</span>
              <span
                v-if="statusConfig[idol.status]"
                class="absolute -bottom-1 -right-1 text-sm leading-none"
                :title="statusConfig[idol.status].label"
              >{{ statusConfig[idol.status].icon }}</span>
            </div>

            <!-- 信息 -->
            <div class="flex-1 min-w-0 text-left">
              <p class="text-text-primary text-sm font-medium truncate">{{ idol.name }}</p>
              <p class="text-text-muted text-xs mb-1.5">Lv.{{ idol.level }}
                <span v-if="getIdolGroup(idol.groupId)" class="ml-1 text-accent-purple opacity-80">· {{ getIdolGroup(idol.groupId) }}</span>
              </p>

              <!-- 精力条 -->
              <div class="flex items-center gap-1.5 mb-1">
                <span class="text-[10px] text-text-muted w-3">精</span>
                <div class="flex-1 h-1.5 bg-bg-elevated rounded-full overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all duration-500"
                    :style="{ width: idol.stats.stamina + '%', background: staminaColor(idol.stats.stamina) }"
                  />
                </div>
                <span class="text-[10px] tabular-nums" :style="{ color: staminaColor(idol.stats.stamina) }">{{ idol.stats.stamina }}</span>
              </div>

              <p class="text-text-muted text-xs">
                <span class="text-primary">♥</span> {{ formatNumber(idol.fans) }}
                <span v-if="idol.scandal > 0" class="ml-2 text-accent-coral">⚠ {{ idol.scandal }}</span>
              </p>
            </div>
          </div>

          <!-- 展开时的属性条 -->
          <div v-if="selectedIdolId === idol.id" class="mt-3 pt-3 border-t border-accent-purple border-opacity-10 space-y-1.5">
            <div v-for="[key, val] in [['\ud83c\udfa4', idol.stats.vocal], ['\ud83d\udc83', idol.stats.dance], ['\u2728', idol.stats.charm]]" :key="String(key)" class="flex items-center gap-2">
              <span class="text-xs w-4 text-center">{{ key }}</span>
              <div class="flex-1 h-1.5 bg-bg-elevated rounded-full overflow-hidden">
                <div class="h-full rounded-full bg-primary transition-all duration-500" :style="{ width: val + '%' }" />
              </div>
              <span class="text-xs tabular-nums text-text-secondary w-6 text-right">{{ val }}</span>
            </div>
          </div>
        </button>

        <!-- 空位 -->
        <button
          @click="showRecruit = true"
          class="w-full border border-dashed border-bg-elevated rounded-lg p-3 text-text-disabled text-xs hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-2"
        >
          <span class="text-lg">+</span>
          <span>招募新成员</span>
        </button>
      </div>
    </div>

    <!-- 右栏：行动 + 团体 + 日志 -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <!-- 顶部：周信息 -->
      <div class="px-6 py-3 border-b border-accent-purple border-opacity-10 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 class="text-text-primary font-display font-bold text-base">
            第 {{ mockAgency.date.year }} 年 · {{ mockAgency.date.month }} 月 · Week {{ mockAgency.date.week }}
          </h2>
          <p class="text-text-muted text-xs mt-0.5">还有 <span class="text-primary font-semibold">{{ mockResources.ap }}</span> 个行动点</p>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-text-disabled text-xs mr-1">预览:</span>
          <button @click="showEvent = true" class="demo-btn">事件</button>
          <button @click="showResult = true" class="demo-btn">结算</button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-5 space-y-5">
        <!-- 行动区 -->
        <div>
          <h3 class="text-text-secondary text-xs font-semibold uppercase tracking-wider mb-3">本周行动</h3>
          <div class="space-y-2">
            <button
              v-for="action in actions"
              :key="action.name"
              @click="action.action()"
              class="action-item w-full"
            >
              <div class="flex items-center gap-3">
                <span class="text-2xl leading-none w-8 text-center flex-shrink-0">{{ action.icon }}</span>
                <div class="flex-1 text-left">
                  <p class="text-text-primary text-sm font-medium">{{ action.name }}</p>
                  <p class="text-text-muted text-xs">{{ action.desc }}</p>
                </div>
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <div class="flex gap-0.5">
                    <span v-for="i in action.cost" :key="i" class="w-2 h-2 rounded-full bg-primary opacity-80" />
                    <span v-for="i in (mockResources.maxAp - action.cost)" :key="'e'+i" class="w-2 h-2 rounded-full bg-bg-elevated" />
                  </div>
                  <span class="text-primary text-xs font-bold">{{ action.cost }} AP</span>
                </div>
              </div>
            </button>
          </div>
        </div>

        <!-- 团体区 -->
        <div v-if="mockGroups.length">
          <h3 class="text-text-secondary text-xs font-semibold uppercase tracking-wider mb-3">活动团体</h3>
          <div class="space-y-2">
            <div
              v-for="group in mockGroups"
              :key="group.id"
              class="bg-bg-surface rounded-xl border border-accent-purple border-opacity-10 p-4 flex items-center gap-4"
            >
              <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent-purple flex items-center justify-center shadow-glow-pink flex-shrink-0">
                <span class="text-xl">✨</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-text-primary font-semibold">{{ group.name }}</p>
                <p class="text-text-muted text-xs">{{ group.memberIds.length }} 名成员 · {{ group.style }}</p>
              </div>
              <div class="text-right flex-shrink-0">
                <p class="text-accent-cyan font-bold tabular-nums">{{ formatNumber(group.fans) }}</p>
                <p class="text-text-muted text-xs">粉丝</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 日志栏 -->
        <div>
          <h3 class="text-text-secondary text-xs font-semibold uppercase tracking-wider mb-3">近期动态</h3>
          <div class="bg-bg-surface rounded-xl border border-accent-purple border-opacity-10 divide-y divide-accent-purple divide-opacity-10">
            <div class="px-4 py-2.5 flex items-start gap-3 text-xs">
              <span class="text-accent-cyan mt-0.5">↑</span>
              <p class="text-text-secondary">星野 凛 完成训练，歌唱 <span class="text-accent-cyan">+5</span></p>
              <span class="text-text-muted ml-auto whitespace-nowrap">3天前</span>
            </div>
            <div class="px-4 py-2.5 flex items-start gap-3 text-xs">
              <span class="text-primary mt-0.5">♥</span>
              <p class="text-text-secondary">Lunaria 举办了小型演出，粉丝 <span class="text-primary">+1,200</span></p>
              <span class="text-text-muted ml-auto whitespace-nowrap">5天前</span>
            </div>
            <div class="px-4 py-2.5 flex items-start gap-3 text-xs">
              <span class="text-accent-coral mt-0.5">⚠</span>
              <p class="text-text-secondary">月見 爱莉 精力过低，建议安排休息</p>
              <span class="text-text-muted ml-auto whitespace-nowrap">6天前</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 弹窗 -->
  <EventScreen v-if="showEvent" @select="showEvent = false" @close="showEvent = false" />
  <RecruitPanel v-if="showRecruit" @recruit="showRecruit = false" @close="showRecruit = false" />
  <ResultScreen v-if="showResult" @continue="showResult = false" />
</template>

<style scoped>
.idol-card {
  @apply bg-bg-surface rounded-xl border border-accent-purple border-opacity-10 p-3 transition-all hover:border-primary hover:border-opacity-30 cursor-pointer text-left;
}
.idol-card--selected {
  @apply border-primary border-opacity-40 bg-primary bg-opacity-5;
}

.action-item {
  @apply bg-bg-surface rounded-xl border border-accent-purple border-opacity-10 px-4 py-3
    hover:border-primary hover:border-opacity-30 hover:bg-bg-elevated
    transition-all cursor-pointer;
}

.demo-btn {
  @apply px-2.5 py-1 bg-bg-surface border border-accent-purple border-opacity-20 text-text-secondary text-xs rounded-lg hover:border-primary hover:text-primary transition-all;
}

.idol-silhouette {
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 56' fill='white'%3E%3Cellipse cx='20' cy='10' rx='8' ry='9'/%3E%3Cpath d='M6 56 C6 36 14 28 20 28 C26 28 34 36 34 56Z'/%3E%3C/svg%3E") center bottom / contain no-repeat;
}
</style>
